import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { GumroadButton } from "./Gumroad";

export default function SamplePages({ book }) {
  const [idx, setIdx] = useState(null);
  const n = book.samples.length;
  const go = (d) => setIdx((i) => (i + d + n) % n);
  const current = idx === null ? null : book.samples[idx];

  return (
    <section className="py-14 md:py-20 border-t border-line" data-testid="ebook-samples">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-4 mb-8">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">Look inside</span>
          <span className="flex-1 h-px bg-line" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">{n} sample pages</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8 grid grid-cols-3 gap-4 md:gap-6">
          {book.samples.map((s, i) => (
            <button
              key={s.page}
              type="button"
              onClick={() => setIdx(i)}
              data-testid={`ebook-sample-thumb-${s.page}`}
              aria-label={`Open sample page ${s.page}`}
              className="group relative block border border-ink bg-paper overflow-hidden hover:shadow-[6px_6px_0_0_#E63946] hover:-translate-y-1 transition-[transform,box-shadow] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal"
            >
              <img src={s.sm} alt={`${book.platform} ebook sample page ${s.page}`} loading="lazy" className="w-full aspect-[397/562] object-cover object-top" />
              <span className="absolute bottom-0 inset-x-0 flex items-center justify-between bg-ink text-paper px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">
                p. {String(s.page).padStart(3, "0")} <Maximize2 size={12} />
              </span>
            </button>
          ))}
        </div>
        <div className="lg:col-span-4">
          <h2 className="font-heading font-black text-3xl md:text-4xl leading-[0.95] tracking-tightest">
            Real pages. <span className="text-signal">No filler.</span>
          </h2>
          <p className="mt-4 text-base text-ink2 leading-relaxed">
            The contents page and two sections from {book.title.replace(/\.$/, "")} — tap any page to read it full size.
          </p>
          <GumroadButton book={book} testId="ebook-samples-buy" location="ebook_samples" className="mt-6 bg-ink text-paper hover:bg-signal px-7 py-4" />
        </div>
        </div>
      </div>

      <Dialog open={idx !== null} onOpenChange={(o) => !o && setIdx(null)}>
        <DialogContent className="max-w-[min(92vw,820px)] p-0 rounded-none border border-ink bg-paper gap-0" data-testid="ebook-sample-dialog">
          <DialogTitle className="sr-only">{book.title} — sample page</DialogTitle>
          <DialogDescription className="sr-only">Preview of a page from the ebook.</DialogDescription>
          {current && (
            <>
              <div className="max-h-[78vh] overflow-y-auto bg-surface">
                <img src={current.lg} alt={`${book.platform} ebook page ${current.page} preview`} className="w-full h-auto" data-testid="ebook-sample-large" />
              </div>
              <div className="flex items-center justify-between border-t border-ink px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em]">
                <button type="button" onClick={() => go(-1)} data-testid="ebook-sample-prev" className="inline-flex items-center gap-1 px-3 py-2 hover:text-signal">
                  <ChevronLeft size={14} /> Prev
                </button>
                <span data-testid="ebook-sample-counter">Page {current.page} · {idx + 1}/{n}</span>
                <button type="button" onClick={() => go(1)} data-testid="ebook-sample-next" className="inline-flex items-center gap-1 px-3 py-2 hover:text-signal">
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
